import React from 'react';
import { ChevronDown, ChevronUp, ArrowUpDown, ArrowUp, ArrowDown, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';

/**
 * Declarative, configurable table with auto-rendering from column config,
 * sortable headers, expandable rows, loading state, and pagination.
 *
 * **Two modes:**
 * - **Legacy (render-prop):** Pass `renderRow` for full control over row markup.
 * - **Declarative (default):** Omit `renderRow`; cells auto-render from `columns[].key`
 *   with optional `columns[].render(value, item, idx)` overrides.
 *
 * @param {{
 *   columns: Array<{
 *     key: string,
 *     label: string,
 *     className?: string,
 *     cellClassName?: string,
 *     render?: (value: any, item: any, idx: number) => React.ReactNode,
 *     sortable?: boolean,
 *   }>,
 *   data: Array<any>,
 *   renderRow?: (item: any, index: number) => React.ReactNode,
 *   getRowId?: (item: any) => any,
 *   onRowClick?: (item: any) => void,
 *   rowClassName?: (item: any, idx: number) => string,
 *   sortKey?: string,
 *   sortDirection?: 'asc' | 'desc',
 *   onSort?: (key: string) => void,
 *   expandedRowId?: any,
 *   onExpandToggle?: (id: any) => void,
 *   renderExpanded?: (item: any) => React.ReactNode,
 *   loading?: boolean,
 *   loadingRows?: number,
 *   emptyMessage?: string,
 *   page?: number,
 *   totalPages?: number,
 *   total?: number,
 *   pageSize?: number,
 *   onPageChange?: (page: number) => void,
 *   headerClassName?: string,
 * }} props
 */
interface DataTableColumn {
  key: string;
  label: string;
  className?: string;
  cellClassName?: string;
  render?: (value: any, item: any, idx: number) => React.ReactNode;
  sortable?: boolean;
}

interface DataTableProps {
  columns: DataTableColumn[];
  data: any[];
  renderRow?: (item: any, index: number) => React.ReactNode;
  getRowId?: (item: any) => any;
  onRowClick?: (item: any) => void;
  rowClassName?: (item: any, idx: number) => string;
  sortKey?: string;
  sortDirection?: 'asc' | 'desc';
  onSort?: (key: string) => void;
  expandedRowId?: any;
  onExpandToggle?: (id: any) => void;
  renderExpanded?: (item: any) => React.ReactNode;
  loading?: boolean;
  loadingRows?: number;
  emptyMessage?: string;
  page?: number;
  totalPages?: number;
  total?: number;
  pageSize?: number;
  onPageChange?: (page: number) => void;
  headerClassName?: string;
  stickyHeader?: boolean;
  maxHeight?: string;
}

export default function DataTable({
  columns,
  data,
  renderRow,
  getRowId = (item: any) => item.id,
  onRowClick,
  rowClassName,
  sortKey,
  sortDirection,
  onSort,
  expandedRowId,
  onExpandToggle,
  renderExpanded,
  loading = false,
  loadingRows = 5,
  emptyMessage = 'Нема податоци.',
  page,
  totalPages,
  total,
  pageSize = 50,
  onPageChange,
  headerClassName = 'border-b bg-muted/50',
  stickyHeader = false,
  maxHeight,
}: DataTableProps) {
  const hasExpand = !!renderExpanded && !!onExpandToggle;
  const allColumns = hasExpand
    ? [...columns, { key: '_expand', label: '', className: 'w-8' }]
    : columns;
  const colSpan = allColumns.length;

  // ── Header cell ──
  const renderHeaderCell = (col) => {
    const isSortable = col.sortable && onSort;
    const isActive = sortKey === col.key;

    const SortIcon = isActive
      ? (sortDirection === 'asc' ? ArrowUp : ArrowDown)
      : ArrowUpDown;

    const inner = (
      <>
        {col.label}
        {isSortable && <SortIcon className="inline ml-1 h-3 w-3 opacity-50" />}
      </>
    );

    return (
      <th
        key={col.key}
        className={`px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider ${
          isSortable ? 'cursor-pointer select-none hover:text-gray-700' : ''
        } ${col.className || ''}`}
        onClick={isSortable ? () => onSort(col.key) : undefined}
      >
        {inner}
      </th>
    );
  };

  // ── Loading skeleton ──
  const renderLoadingRows = () =>
    Array.from({ length: loadingRows }, (_, i) => (
      <tr key={`skel-${i}`} className="border-b">
        {allColumns.map((col) => (
          <td key={col.key} className="px-3 py-2">
            <Skeleton className="h-4 w-full" />
          </td>
        ))}
      </tr>
    ));

  // ── Auto-render a single data cell ──
  const renderCell = (col, item, idx) => {
    if (col.key === '_expand') {
      const id = getRowId(item);
      return expandedRowId === id
        ? <ChevronUp className="h-4 w-4" />
        : <ChevronDown className="h-4 w-4" />;
    }
    const value = item[col.key];
    return col.render ? col.render(value, item, idx) : (value ?? '-');
  };

  // ── Auto-render rows (declarative mode) ──
  const renderAutoRows = () =>
    data.map((item, idx) => {
      const id = getRowId(item);
      const isExpanded = hasExpand && expandedRowId === id;
      const clickHandler = onRowClick
        ? () => onRowClick(item)
        : hasExpand
        ? () => onExpandToggle(id)
        : undefined;

      return (
        <React.Fragment key={id}>
          <tr
            className={`border-b transition-colors ${
              clickHandler ? 'cursor-pointer hover:bg-muted/30' : ''
            } ${rowClassName ? rowClassName(item, idx) : ''}`}
            onClick={clickHandler}
          >
            {allColumns.map((col) => (
              <td key={col.key} className={`px-3 py-2 ${col.cellClassName || ''}`}>
                {renderCell(col, item, idx)}
              </td>
            ))}
          </tr>
          {isExpanded && (
            <tr>
              <td colSpan={colSpan} className="bg-muted/20 px-6 py-3">
                {renderExpanded(item)}
              </td>
            </tr>
          )}
        </React.Fragment>
      );
    });

  return (
    <>
      <div
        className="overflow-x-auto rounded-md border"
        style={maxHeight ? { maxHeight, overflowY: 'auto' } : undefined}
      >
        <table className="w-full text-sm">
          <thead className={`${headerClassName}${stickyHeader ? ' sticky top-0 z-10' : ''}`}>
            <tr>{allColumns.map(renderHeaderCell)}</tr>
          </thead>
          <tbody className="bg-background divide-y divide-border">
            {loading ? (
              renderLoadingRows()
            ) : data.length === 0 ? (
              <tr>
                <td colSpan={colSpan} className="px-4 py-8 text-center text-gray-500">
                  {emptyMessage}
                </td>
              </tr>
            ) : renderRow ? (
              data.map((item, idx) => renderRow(item, idx))
            ) : (
              renderAutoRows()
            )}
          </tbody>
        </table>
      </div>

      {totalPages > 1 && onPageChange && (
        <div className="flex items-center justify-between mt-4">
          <span className="text-sm text-muted-foreground">
            Прикажани {(page - 1) * pageSize + 1}–{Math.min(page * pageSize, total)} од {total}
          </span>
          <div className="flex gap-1">
            <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => onPageChange(page - 1)}>
              Претходна
            </Button>
            <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => onPageChange(page + 1)}>
              Следна
            </Button>
          </div>
        </div>
      )}
    </>
  );
}
